import map from 'lodash/map';
import flatten from 'lodash/flatten';
import filter from 'lodash/filter';
import get from 'lodash/get';
import findIndex from 'lodash/findIndex';
import unionBy from 'lodash/unionBy';
import find from 'lodash/find';
import { resolveErrorMessage } from 'modules/errors';
import {
  getApplications,
  getApplicationsByAuditionId,
  setAuditionApplicationUnreadStatus,
  loadApplicationForTalent,
  loadAuditionApplications,
} from 'modules/audition-applications';
import { openChatPopup, closeChatPopup, ChatType } from 'modules/chat';
import { createAudioClip } from '../audio-clips';
import {
  postAuditionMessage,
  fetchAllAuditionMessages,
  fetchApplicationMessages,
  fetchUnreadMessagesCount,
} from './api';
import {
  auditionApplicationChatFilter,
  auditionApplicationChatId,
} from './services';
import {
  createAsyncThunk,
  createSlice,
  createSelector,
} from '@reduxjs/toolkit';

export const createAuditionMessage = createAsyncThunk(
  'auditionMessages/create',
  async ({ auditionId, applicationId, authorId, message }) =>
    await postAuditionMessage(auditionId, applicationId, authorId, message),
);

export const loadAllAuditionMessages = createAsyncThunk(
  'auditionMessages/loadMessages',
  async ({ auditionId, applications }) =>
    await fetchAllAuditionMessages(auditionId, applications),
);

export const loadApplicationMessages = createAsyncThunk(
  'auditionMessages/loadApplicationMessages',
  async ({ auditionId, applicationId }) =>
    await fetchApplicationMessages(auditionId, applicationId),
);

export const loadUnreadMessagesCount = createAsyncThunk(
  'auditionMessages/loadUnreadMessagesCount',
  async _ => await fetchUnreadMessagesCount(),
);

export const markAuditionApplicationAsRead = (
  auditionId,
  applicationId,
  talentId,
) => dispatch => {
  return dispatch(
    setAuditionApplicationUnreadStatus({
      auditionId,
      applicationId,
      unreadMessageForAccountId: null,
    }),
  ).then(() =>
    Promise.all([
      dispatch(loadApplicationForTalent({ auditionId, talentId })),
      dispatch(loadUnreadMessagesCount()),
    ]),
  );
};

export const loadAuditionApplicationMessaging = (
  accountId,
  auditionId,
  talentId,
) => dispatch =>
  dispatch(loadApplicationForTalent({ auditionId, talentId })).then(
    response => {
      const applicationId = get(response.payload, 'data.id');
      const unreadMessageAccountId = get(
        response.payload,
        'data.unreadMessageAccountId',
      );

      return Promise.all([
        dispatch(loadApplicationMessages({ auditionId, applicationId })),
        unreadMessageAccountId === accountId &&
          dispatch(
            markAuditionApplicationAsRead(auditionId, applicationId, talentId),
          ),
      ]);
    },
  );

export const refreshAuditionMessagingWindow = (accountId, chatWindow) => {
  const { auditionId, talentId } = chatWindow;
  return loadAuditionApplicationMessaging(accountId, auditionId, talentId);
};

export function openAuditionApplicationChatPopup(auditionId, talentId) {
  return openChatPopup(
    auditionApplicationChatId(auditionId, talentId),
    ChatType.AUDITION,
    { auditionId, talentId },
    auditionApplicationChatFilter(auditionId, talentId),
  );
}

export function closeAuditionApplicationChatPopup(auditionId, talentId) {
  return closeChatPopup(auditionApplicationChatFilter(auditionId, talentId));
}

const auditionMessagesSlice = createSlice({
  name: 'auditionMessages',
  initialState: {
    messages: [],
    unreadMessagesCount: 0,
    loading: false,
    submitError: null,
    submissionsSort: '-createdAt',
  },
  reducers: {
    setSubmissionsSort: (state, action) => {
      // original action type is 'auditionMessages/sortSubmissions/set',
      state.submissionsSort = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(createAuditionMessage.fulfilled, (state, action) => {
        const newMessage = action.payload.data;

        state.messages = [...state.messages, newMessage];
        state.submitError = false;
      })
      .addCase(createAuditionMessage.rejected, (state, action) => {
        state.submitError = resolveErrorMessage(action.error);
      })
      .addCase(loadAllAuditionMessages.pending, (state, _) => {
        state.loading = true;
      })
      .addCase(loadAllAuditionMessages.fulfilled, (state, action) => {
        state.loading = false;

        if (action.payload === null) {
          return;
        }
        const auditionMessages = map(
          action.payload,
          application => application.data,
        );

        state.messages = unionBy(
          flatten(auditionMessages),
          state.messages,
          'id',
        );
      })
      .addCase(loadAllAuditionMessages.rejected, (state, _) => {
        state.loading = false;
      })
      .addCase(loadApplicationMessages.fulfilled, (state, action) => {
        const newMessages = action.payload.data;

        // state.messages MUST come first so that existing messages are not
        // reported as "new" due to being new objects
        state.messages = unionBy(state.messages, newMessages, 'id');
      })
      .addCase(loadUnreadMessagesCount.fulfilled, (state, action) => {
        state.unreadMessagesCount = get(
          action,
          'payload.data.unreadMessageApplicationCount',
        );
      })
      .addCase(createAudioClip.fulfilled, (state, action) => {
        const auditionMessageId = get(action, 'payload.data.auditionMessageId');
        const messageAudioClip = get(action, 'payload.data');

        const messageIndex = findIndex(
          state.messages,
          msg => msg.id === auditionMessageId,
        );

        if (messageIndex !== -1) {
          state.messages[messageIndex].audioClip = { ...messageAudioClip };
        }
      })
      .addCase(loadAuditionApplications.fulfilled, (state, action) => {
        const applications = action.payload.data;

        // TODO: see if we can make this cleaner; async thunk appears to return immutable objects
        applications.forEach(application => {
          const updatedMessages = application.auditionMessages.map(
            auditionMessage => {
              if (auditionMessage.authorAccount.talentId) {
                const deepCopy = {
                  ...auditionMessage,
                  authorAccount: { ...auditionMessage.authorAccount },
                };
                deepCopy.authorAccount.talent = application.talent;
                return deepCopy;
              }
              return auditionMessage;
            },
          );

          state.messages = unionBy(updatedMessages, state.messages, 'id');
        });
      });
  },
  selectors: {
    getSubmitError: state => state.submitError,
    getMessages: state => state.messages,
    getUnreadMessagesCount: state => state.unreadMessagesCount,
    getMessagesLoading: state => state.loading,
    getSubmissionsSort: state => state.submissionsSort,
  },
});

export const { setSubmissionsSort } = auditionMessagesSlice.actions;
export const reducer = auditionMessagesSlice.reducer;
export const {
  getSubmitError,
  getMessages,
  getUnreadMessagesCount,
  getMessagesLoading,
  getSubmissionsSort,
} = auditionMessagesSlice.selectors;

export const getSubmissions = createSelector(
  getMessages,
  stateMessages =>
    filter(stateMessages, message => !!get(message, ['audioClip'])),
);

export const getExchangedMessages = (talentId, producerId, auditionId) =>
  createSelector(
    getMessages,
    getApplications,
    (stateMessages, stateApplications) => {
      return filter(stateMessages, message => {
        if (!message || !message.authorAccount) {
          return null;
        }

        const application = find(
          stateApplications,
          app => app.id === message.auditionApplicationId,
        );

        if (!application) {
          return (
            message.authorAccount.talentId === talentId ||
            message.authorAccount.producerId === producerId
          );
        }

        return (
          message.auditionApplication.auditionId === auditionId &&
          application.talentId === talentId &&
          (message.authorAccount.talentId === talentId ||
            message.authorAccount.producerId === producerId)
        );
      });
    },
  );

export const getMessagesByApplicationId = applicationId =>
  createSelector(
    getMessages,
    messages => {
      return filter(
        messages,
        message => message.auditionApplicationId === applicationId,
      );
    },
  );

export const getSubmissionsByAuditionId = auditionId =>
  createSelector(
    getSubmissions,
    getApplicationsByAuditionId(auditionId),
    (stateSubmissions, auditionApplications) => {
      const submissions = map(auditionApplications, auditionApplication =>
        filter(
          stateSubmissions,
          submission =>
            submission.auditionApplicationId === auditionApplication.id,
        ),
      );

      return flatten(submissions);
    },
  );
